import { Chain } from '@1delta/chain-registry'
import { getEvmClient } from '@1delta/providers'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Lair Finance stKAIA (Kaia chain 8217) intrinsic yield.
 *
 * stKAIA is non-rebasing and exposes no APR getter and no public APR
 * API (DefiLlama's yields dataset has no Kaia coverage). The value
 * accrues purely through `getRatio()` (1e18-scaled KAIA-per-stKAIA), so
 * the APR is derived from the rate's growth over a historical window:
 *
 *   APR = (rNow / rOld - 1) * (yearSeconds / elapsedSeconds) * 100
 *
 * Block timestamps are read directly so the annualisation uses real
 * elapsed time rather than an assumed block interval.
 *
 * Robustness: a non-archival RPC cannot serve the historical
 * `getRatio()` read (the state trie node is pruned). We therefore try a
 * few look-back windows (7d → 3d → 1d) across several Kaia RPCs and
 * return the first success. The 7d window is preferred because rewards
 * land in discrete steps, making a 1d sample noisy. If every attempt
 * fails the fetcher resolves to `0` (never throws) so it cannot sink the
 * aggregate yields fetch.
 */

const STKAIA = '0x42952b873ed6f7f0a7e4992e2a9818e3a9001995'
const GET_RATIO_ABI = [
  {
    name: 'getRatio',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** Look-back windows in blocks (~1s Kaia block time): 7d → 3d → 1d → 12h.
 *  Tried longest-first (smoother APR, since rewards land in discrete
 *  steps), falling back to shorter windows when an RPC can't serve the
 *  older historical state (pruned trie). The 12h tail keeps a result
 *  available even on shallow-archival public nodes. */
const WINDOWS_BLOCKS = [604_800n, 259_200n, 86_400n, 43_200n]
/** How many Kaia RPC endpoints to try per window. */
const RPC_ATTEMPTS = 4
/** Overall wall-clock budget for the whole sampling routine. */
const TOTAL_TIMEOUT_MS = 8_000

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('lair stKAIA yield timeout')), ms),
    ),
  ])

const sampleApr = async (): Promise<number> => {
  for (const window of WINDOWS_BLOCKS) {
    for (let rpcId = 0; rpcId < RPC_ATTEMPTS; rpcId++) {
      try {
        const client = getEvmClient(Chain.KAIA_MAINNET, rpcId)
        const latest = await client.getBlockNumber()
        if (latest <= window) continue
        const oldBlock = latest - window

        const [rNow, rOld, blockNow, blockOld] = await Promise.all([
          client.readContract({
            address: STKAIA,
            abi: GET_RATIO_ABI,
            functionName: 'getRatio',
            blockNumber: latest,
          }),
          client.readContract({
            address: STKAIA,
            abi: GET_RATIO_ABI,
            functionName: 'getRatio',
            blockNumber: oldBlock,
          }),
          client.getBlock({ blockNumber: latest }),
          client.getBlock({ blockNumber: oldBlock }),
        ])

        const rNowB = rNow as bigint
        const rOldB = rOld as bigint
        const elapsed = Number(blockNow.timestamp - blockOld.timestamp)
        if (rOldB <= 0n || rNowB <= rOldB || elapsed <= 0) continue

        const apr = annualizeRateDeltaPercent(rNowB, rOldB, elapsed)
        if (Number.isFinite(apr) && apr > 0) return apr
      } catch {
        // pruned node / RPC error — try the next endpoint or window.
      }
    }
  }
  return 0
}

export const stKaiaFetcher: YieldFetcher = {
  label: 'STKAIA',
  fetch: async () => {
    try {
      return { STKAIA: await withTimeout(sampleApr(), TOTAL_TIMEOUT_MS) }
    } catch {
      return { STKAIA: 0 }
    }
  },
}
